import { useEffect, useState } from "react";

import type { AgentConversationDto, AgentNoteDto, CustomerDto } from "../api/types.js";
import { useSession } from "../session/SessionContext.js";

/**
 * Who the agent is talking to, what they have bought, and what colleagues noted.
 *
 * ## Why orders and rewards are fetched per conversation
 *
 * Both are keyed by **conversation**, not by customer, because most of the people
 * in this queue have no customer record at all. They bought as guests. Their orders
 * belong to a billing email and their Mahalo balance sits in a ledger whose
 * `user_id` column is null for nearly every row — email is the identity, and the
 * conversation is where the platform holds one it is willing to trust.
 *
 * ## Why an empty state is never a zero
 *
 * "This customer has no orders" and "we could not reach the shop" look identical if
 * both render as an empty list, and an agent who reads the second as the first will
 * tell a customer their order does not exist. Each failure below says which one it
 * is, in words.
 */

interface OrderRow {
  readonly orderId: string;
  readonly orderNumber: string;
  readonly status: string;
  readonly total: number;
  readonly currency: string;
  readonly placedAt: string | null;
}

interface RewardsRow {
  readonly balance: number | null;
  readonly pending: number | null;
}

/** What a lookup came back with. `null` while it is still in flight. */
type Lookup<T> =
  | { readonly state: "loading" }
  | { readonly state: "ready"; readonly value: T }
  | { readonly state: "none"; readonly why: string };

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    // An unknown currency code must not take the panel down with it.
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Turn a failed lookup into something an agent can act on.
 *
 * The status is the whole message here: 403 means there is nobody to look up, 404
 * means we looked and found nothing, 503 means the shop did not answer. Those lead
 * to three different things an agent should say next.
 */
/**
 * Who the agent is talking to, from the best source available.
 *
 * ## Why the account comes first, and why this exists at all
 *
 * A signed-in shopper never fills in the pre-chat form — the widget does not ask
 * somebody who is already known — so a panel that reads only `preChat` shows nothing
 * at all for exactly the people this platform knows the most about. That was the
 * bug: an agent looking at a customer with an account, an email and a phone number on
 * file saw the word "Customer" and four empty rows.
 *
 * The account is also *fresher* than anything on the conversation. A pre-chat form is
 * what somebody typed once, at the start; the account is synchronised from the shop
 * and is what their order confirmations actually go to.
 *
 * The phone lives on the billing address rather than as a field of its own, because
 * that is where WooCommerce keeps it. Shipping is the fallback for the few accounts
 * that carry one there and not the other.
 */
function contactOf(
  conversation: AgentConversationDto,
  account: CustomerDto | null,
): { readonly name?: string; readonly email?: string; readonly phone?: string } {
  const fromAccount =
    account === null
      ? undefined
      : (account.displayName?.trim() ??
        `${account.firstName ?? ""} ${account.lastName ?? ""}`.trim());

  const stored = conversation.requesterDisplayName?.trim();
  const preChat = conversation.preChat;
  const typed =
    preChat === undefined ? "" : `${preChat.firstName} ${preChat.lastName}`.trim();

  const name =
    fromAccount !== undefined && fromAccount !== ""
      ? fromAccount
      : stored !== undefined && stored !== ""
        ? stored
        : typed === ""
          ? undefined
          : typed;

  const email = account?.email ?? preChat?.email;
  const phone = account?.billing?.phone ?? account?.shipping?.phone ?? preChat?.phone;

  return {
    ...(name !== undefined ? { name } : {}),
    ...(email !== undefined && email !== "" ? { email } : {}),
    ...(phone !== undefined && phone !== "" ? { phone } : {}),
  };
}

function whyNot(cause: unknown, missing: string): string {
  const status =
    typeof cause === "object" && cause !== null && "status" in cause ? Number(cause.status) : 0;

  if (status === 403) return "No account, and no email was given before the chat.";
  if (status === 404) return missing;
  if (status === 503) return "The shop did not answer. This is not a statement about the customer.";

  return "This could not be loaded.";
}
export function CustomerPanel({
  conversation,
  notes,
  onAddNote,
}: {
  readonly conversation: AgentConversationDto;
  readonly notes: readonly AgentNoteDto[];
  readonly onAddNote: (body: string) => Promise<void>;
}) {
  const { api } = useSession();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState<Lookup<readonly OrderRow[]>>({ state: "loading" });
  const [rewards, setRewards] = useState<Lookup<RewardsRow>>({ state: "loading" });

  const [account, setAccount] = useState<Lookup<CustomerDto>>({ state: "loading" });

  const conversationId = conversation.conversationId;
  const customerId =
    conversation.requester.type === "customer" ? (conversation.requester.customerId ?? null) : null;

  /**
   * The account behind a signed-in conversation.
   *
   * Keyed by customer rather than by conversation, unlike the two lookups below,
   * because that is the route that exists and the identity is the customer's rather
   * than the chat's. A guest has no account to fetch and is not asked about — the
   * pre-chat form is everything the platform has for them.
   */
  useEffect(() => {
    if (customerId === null) {
      setAccount({ state: "none", why: "This visitor is not signed in." });
      return undefined;
    }

    const open = new AbortController();

    setAccount({ state: "loading" });

    void (async () => {
      try {
        const answer = await api.get<CustomerDto>(
          `/support/customers/${encodeURIComponent(customerId)}`,
        );

        if (!open.signal.aborted) setAccount({ state: "ready", value: answer });
      } catch (cause) {
        if (!open.signal.aborted) {
          setAccount({
            state: "none",
            why: whyNot(cause, "This account has not synchronised from the shop yet."),
          });
        }
      }
    })();

    return () => {
      open.abort();
    };
  }, [api, customerId]);

  useEffect(() => {
    const open = new AbortController();

    setOrders({ state: "loading" });
    setRewards({ state: "loading" });

    void (async () => {
      try {
        const answer = await api.get<{ orders?: readonly OrderRow[] }>(
          `/support/conversations/${conversationId}/orders`,
        );

        if (open.signal.aborted) return;

        // Guarded rather than trusted. This panel sits beside a live conversation,
        // and a body that is not the shape we expected must degrade to a line of
        // text rather than take the agent's whole sidebar down with it.
        setOrders(
          Array.isArray(answer.orders)
            ? { state: "ready", value: answer.orders }
            : { state: "none", why: "This could not be loaded." },
        );
      } catch (cause) {
        if (!open.signal.aborted) {
          setOrders({ state: "none", why: whyNot(cause, "No orders found for this customer.") });
        }
      }
    })();

    void (async () => {
      try {
        /*
          `| null` is not defensive typing — it is what the route sends. A customer
          with no rewards account comes back as an explicit `rewards: null`, and
          declaring it optional-only told the compiler the guard below was dead code.
        */
        const answer = await api.get<{ rewards?: RewardsRow | null }>(
          `/support/conversations/${conversationId}/rewards`,
        );

        if (open.signal.aborted) return;

        const value = answer.rewards;

        /*
          `!= null` rather than `typeof value === "object"`, which was wrong in the
          one way that matters: `typeof null` is `"object"`, so a body carrying an
          explicit null passed the guard and every read of it threw — taking the
          agent's whole sidebar down beside a live conversation.
        */
        setRewards(
          value !== null && value !== undefined
            ? { state: "ready", value }
            : { state: "none", why: "No Mahalo Rewards account." },
        );
      } catch (cause) {
        if (!open.signal.aborted) {
          setRewards({ state: "none", why: whyNot(cause, "No Mahalo Rewards account.") });
        }
      }
    })();

    return () => {
      open.abort();
    };
  }, [api, conversationId]);

  const contact = contactOf(conversation, account.state === "ready" ? account.value : null);

  async function addNote(): Promise<void> {
    const body = draft.trim();
    if (body.length === 0 || saving) return;

    setSaving(true);

    try {
      await onAddNote(body);
      setDraft("");
    } finally {
      setSaving(false);
    }
  }

  return (
    <aside className="sidebar" aria-label="Customer details">
      <section className="section">
        {/*
          The person's name is the heading, and the word "Customer" is what is left
          when there is genuinely no name — not a title printed over their details.
        */}
        <h3>
          {contact.name ?? (conversation.requester.type === "guest" ? "Guest" : "Customer")}
        </h3>
        <dl style={{ margin: 0 }}>
          <div className="field">
            <dt>Type</dt>
            <dd>{conversation.requester.type === "guest" ? "Guest" : "Signed in"}</dd>
          </div>

          {contact.name !== undefined && (
            <div className="field">
              <dt>Name</dt>
              <dd>{contact.name}</dd>
            </div>
          )}

          {contact.email !== undefined && (
            <div className="field">
              <dt>Email</dt>
              <dd>{contact.email}</dd>
            </div>
          )}

          {contact.phone !== undefined && (
            <div className="field">
              <dt>Phone</dt>
              <dd>{contact.phone}</dd>
            </div>
          )}

          {/*
            Said in words rather than left as three empty rows. "We have no phone
            number for them" and "their account has not synchronised" lead an agent
            to do different things, and an absent row says neither.
          */}
          {customerId !== null && account.state === "none" && (
            <div className="field">
              <dt>Account</dt>
              <dd>{account.why}</dd>
            </div>
          )}

          <div className="field">
            <dt>Started</dt>
            <dd>{new Date(conversation.createdAt).toLocaleString()}</dd>
          </div>

          <div className="field">
            <dt>Channel</dt>
            <dd>{conversation.channel}</dd>
          </div>
        </dl>

        {conversation.tags.length > 0 && (
          <div className="row" style={{ marginTop: 10, flexWrap: "wrap" }}>
            {conversation.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag.replaceAll("_", " ")}
              </span>
            ))}
          </div>
        )}
      </section>

      {/*
        The assistant fills this in when an agent is about to join. Nothing generates
        it yet, so the section appears only when there is something to show rather
        than as a permanently empty box.
      */}
      {conversation.summary !== undefined && (
        <section className="section">
          <h3>Summary</h3>
          <p style={{ margin: 0 }}>{conversation.summary}</p>
        </section>
      )}

      {conversation.escalationReason !== undefined && (
        <section className="section">
          <h3>Escalation</h3>
          <div className="field">
            <dt>Reason</dt>
            <dd>{conversation.escalationReason.replaceAll("_", " ")}</dd>
          </div>
          {conversation.outsideBusinessHours === true && (
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              Requested outside business hours.
            </p>
          )}
        </section>
      )}

      <section className="section">
        <h3>Mahalo Rewards</h3>
        {rewards.state === "loading" && (
          <p className="small muted" style={{ margin: 0 }}>
            Loading…
          </p>
        )}
        {rewards.state === "none" && (
          <p className="small muted" style={{ margin: 0 }}>
            {rewards.why}
          </p>
        )}
        {rewards.state === "ready" && (
          <dl style={{ margin: 0 }}>
            <div className="field">
              <dt>Available</dt>
              <dd>
                {rewards.value.balance === null
                  ? // Not zero. No source configured is a different fact from a spent
                    // balance, and only one of them is worth telling a customer.
                    "Not tracked"
                  : money(rewards.value.balance, "USD")}
              </dd>
            </div>
            <div className="field">
              <dt>Pending</dt>
              <dd>
                {rewards.value.pending === null
                  ? "Not tracked"
                  : money(rewards.value.pending, "USD")}
              </dd>
            </div>
          </dl>
        )}
      </section>

      <section className="section">
        <h3>Orders</h3>
        {orders.state === "loading" && (
          <p className="small muted" style={{ margin: 0 }}>
            Loading…
          </p>
        )}
        {orders.state === "none" && (
          <p className="small muted" style={{ margin: 0 }}>
            {orders.why}
          </p>
        )}
        {orders.state === "ready" &&
          (orders.value.length === 0 ? (
            <p className="small muted" style={{ margin: 0 }}>
              No orders under this account or email.
            </p>
          ) : (
            <dl style={{ margin: 0 }}>
              {orders.value.map((order) => (
                <div className="field" key={order.orderId}>
                  <dt>{order.orderNumber}</dt>
                  <dd>
                    {money(order.total, order.currency)} · {order.status.replaceAll("_", " ")}
                    {order.placedAt !== null && (
                      <span className="muted">
                        {" "}
                        · {new Date(order.placedAt).toLocaleDateString()}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          ))}
      </section>

      <section className="section">
        <h3>Internal notes</h3>
        <p className="small muted" style={{ marginTop: 0 }}>
          Never visible to the customer.
        </p>

        {notes.map((note) => (
          <div key={note.noteId} className="note">
            {note.body}
            <div className="meta">
              {note.authorDisplayName} · {new Date(note.createdAt).toLocaleString()}
            </div>
          </div>
        ))}

        <textarea
          value={draft}
          placeholder="Add a note for colleagues…"
          aria-label="New internal note"
          rows={3}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <button
          style={{ marginTop: 8 }}
          disabled={saving || draft.trim().length === 0}
          onClick={() => {
            void addNote();
          }}
        >
          {saving ? "Saving…" : "Add note"}
        </button>
      </section>

      <section className="section">
        <h3>Not yet available</h3>
        <p className="small muted" style={{ margin: 0 }}>
          Previous conversations and coupons used will appear here once those integrations are
          connected. This panel shows nothing rather than showing zeroes, so an empty section is
          never mistaken for a customer with no history.
        </p>
      </section>
    </aside>
  );
}
